
import type { PropType } from 'vue'
import type { Route } from 'vue-router'
import type { FeatureIdEnum } from '../enums/feature-id.enum'

import type { WidgetOptions, WidgetOptionsItem } from '../models'
import type { RadioItem } from './radio-group.component.vue'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { ColorEnum } from '../enums'

import { APP_CONSTS } from '../store/app/app.consts'

import { FeaturesTreeUtils } from '../utils'
import { UiPopover, UiTooltip } from './keen-ui'
import MutableList from './mutable-list.component.vue'
import EpbRadioGroup from './radio-group.component.vue'
import { EpbBadge } from './shared/epb-badge'
import { EpbButton } from './shared/epb-button'
import { EpbSwitch } from './shared/epb-switch'
import { EpbText } from './shared/epb-text'

export default defineComponent({
  name: 'Widget',
  components: {
    EpbBadge,
    EpbButton,
    EpbRadioGroup,
    EpbSwitch,
    EpbText,
    MutableList,
    UiTooltip,
    UiPopover,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    title: String,
    subtitle: String,
    descriptionTooltipInfo: String,
    redirectTo: String as PropType<FeatureIdEnum>,
    options: {
      type: Object as PropType<WidgetOptions>,
      default: () => ({
        items: [],
      }),
    },
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    redirectionRoute(): Route {
      const featureNode = FeaturesTreeUtils.getFeatureNode(
        this.redirectTo,
        this.featuresTree[DomainEnum.SEARCH],
      )
      return {
        ...this.$route,
        name: featureNode.routeName,
      }
    },
    showDropdown(): boolean {
      return !!this.options.items.length || !!this.options.documentationLink
    },
    radioGroupOptions(): Array<RadioItem> {
      return this.options.items.map((item: WidgetOptionsItem) => {
        return {
          id: item.id,
          value: item.label,
          checked: item.selected,
        }
      })
    },
  },
  setup() {
    return {
      ColorEnum,
    }
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-widget',
      messages: this.$epb.messages.highlights,
    }
  },
  methods: {
    onOptionsChange(id: string): void {
      const newOptions = {
        ...this.options,
        items: this.options.items.map((item: WidgetOptionsItem): WidgetOptionsItem => {
          return {
            ...item,
            selected: this.updateSelected(item, id),
          }
        }),
      }
      if (!this.options.multiSelect) {
        this.$refs.optionsDropdown.close()
      }
      this.$emit('onOptionsChange', newOptions)
    },
    updateSelected(item: WidgetOptionsItem, id: string): boolean {
      const isItemSelected = item.id === id
      if (this.options.multiSelect) {
        return isItemSelected ? !item.selected : item.selected
      }
      return isItemSelected
    },
    onMoveItem({
      item: itemToMove,
      targetIndex,
    }: {
      item: WidgetOptionsItem
      targetIndex: number
    }): void {
      const updatedElements = [...this.options.items]
      const oldIndex = this.getIndexOfItem(itemToMove)
      updatedElements.splice(oldIndex, 1)
      updatedElements.splice(targetIndex, 0, itemToMove)
      const newOptions = {
        ...this.options,
        items: updatedElements,
      }
      this.$emit('onOptionsChange', newOptions)
    },
    getIndexOfItem(search: WidgetOptionsItem): number {
      return this.options.items.findIndex((item: WidgetOptionsItem) => item.id === search.id)
    },
  },
})
